"use client";
import React, { useState } from "react";
import CardGrid from "../../CardGrid";
import ReviewParent from "../design1/components/Review";
import Switch from "./components/ButtonSwitch";
import { ShowMoreText } from "../design1/About1";
import { CardVariant2 } from "./components/CardVariant2";
import { BranchSummaryCard } from "../design1/Branches";
import Button from "../../ui/Button";
import { Branch } from "../../constants/branchData";
import { flattenRichText } from "../design1/About1";
import { useBranches } from "src/app/lib/fetchBranches";
import HeroPage2 from "./components/HeroPage2";
import { ParentCard } from "../design1/PropertyPage1";

import { PropertyCardSkeleton } from "./components/Skeletons/Design2PropertyCard";
import { findPropertiesByTypeOptional } from "../../constants/mockProperties";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";
import BranchCardSkeleton from "./components/Skeletons/BranchSkeleton";
const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
import { Config, PropertyType } from "src/types";
import { useFetchReviews } from "src/app/lib/useFetchReview";
import { ReviewType } from "../../constants/mockPosts";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
const AboutUs2 = ({ listings }: { listings: PropertyType[] }) => {
  const { data: info, isLoading } = useFetchCompanyInfo();

  return (
    <section className="w-full max-w-full overflow-x-clip">
      <HeroPage2 bgSize="cover" bgImage="/images/wavy-lines.webp">
        <div className="wrapper text-white flex flex-col items-center">
          <h1 className="mb-4">About Us & Reviews</h1>
          <p>
            Explore our range of professional real estate services designed to
            meet your every need.
          </p>
        </div>
      </HeroPage2>
      <div className="wrapper  py-10">
        <div className="grid-container">
          <FirstCol info={info} loading={isLoading} />
          <SecondCol listing={listings} loading={isLoading} info={info} />
        </div>
      </div>
    </section>
  );
};
export default AboutUs2;

const FirstCol: React.FC<{ info?: Config; loading: boolean }> = ({
  info,
  loading,
}) => {
  const [activeBranch, setActiveBranch] = useState<1 | 2>(1); // Default is 1

  return (
    <div>
      <Switch
        button1Text="About Us"
        button2Text="Reviews"
        activeState={activeBranch}
        onSwitch={(newState) => setActiveBranch(newState)}
      />
      <div className="items pt-5">
        {activeBranch === 1 ? (
          <FirstItem
            loadingText={loading}
            desc={info?.companyInfo?.about as string}
          />
        ) : (
          <SecondItem />
        )}
      </div>
    </div>
  );
};
const SecondCol: React.FC<{
  info?: Config;
  loading?: boolean;
  listing: PropertyType[];
}> = ({ info, loading, listing }) => {
  const summary = info?.companyStat;
  const isLoading = false;
  const isError = false;
  const properties = findPropertiesByTypeOptional();
  const dataToMap = isDemo ? properties : listing;

  return (
    <div className="flex scrollbar-hide overflow-scroll sticky top-[70px] !max-h-[800px] flex-col gap-8 ">
      <BranchSummaryCard
        loading={loading}
        recent
        second
        {...{ title: "Company Summary", values: { ...summary } }}
      />
      <div className="recent bg-white shadow-md p-4 rounded-xl  gap-8">
        <div className="first py-2 flex items-center justify-between">
          <h4 className="text-secondary-main font-semibold">Recent Listings</h4>
          {dataToMap?.length > 0 ? (
            <Button
              variant="new"
              className="text-themeColor"
              href="/properties"
            >
              See More
            </Button>
          ) : null}
        </div>

        <CardGrid maxColumns={2}>
          {(() => {
            if (isDemo && dataToMap?.length) {
              return dataToMap.map((item, index) => (
                <CardVariant2 className="!h-full block" key={index} {...item} />
              ));
            }

            if (isLoading) {
              return Array.from({ length: 8 }).map((_, index) => (
                <PropertyCardSkeleton key={index} />
              ));
            }

            if (isError) {
              return (
                <div className="text-red-500">
                  There was an error loading properties.
                </div>
              );
            }

            if (dataToMap?.length) {
              return dataToMap.map((item, index) => (
                <CardVariant2 className="!h-full block" key={index} {...item} />
              ));
            }

            return (
              <div className="text-gray-500">
                {" "}
                Looks like there haven’t been any new property updates yet.
              </div>
            );
          })()}
        </CardGrid>
      </div>
    </div>
  );
};
interface BranchExtra extends Branch {
  variant?: boolean;
}

export const BranchCard: React.FC<BranchExtra> = ({
  coverImage,
  id,
  branchName,
  address,
  aboutBranch,
  variant,
}) => {
  const slugwithid = generateSlugWithId(Number(id), branchName);

  return (
    <div
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className="parent bg-white sm:p-4 p-8 rounded-xl shadow-md"
    >
      <div className="flex gap-6">
        {!variant && (
          <Image
            height={192}
            width={192}
            src={coverImage}
            alt=""
            className="first size-[12rem] xs:w-[50%] min-h-full block aspect-square object-cover rounded-md"
          />
        )}

        <div
          className={`flex flex-col w-full  justify-between ${
            variant && "gap-5"
          }`}
        >
          <div className="flex w-full justify-between">
            <div className={`first ${variant && "flex gap-8"}`}>
              {variant && (
                <Image
                  height={112}
                  width={112}
                  src={coverImage}
                  alt=""
                  className="first h-[7rem] min-h-full block aspect-square object-cover rounded-md"
                />
              )}
              <div className="i">
                <h3 className="font-semibold text-secondary-main">
                  {branchName}
                </h3>
                <h5 className="text-label-main flex items-center gap-1">
                  <div className="w-auto">
                    {variant ? <AddressIconVariant /> : <AddressIcon />}
                  </div>
                  <span className="line-clamp-1">{address}</span>
                </h5>
              </div>
            </div>
            {variant && (
              <Button
                href={`/branches/?branch=${slugwithid}`}
                className="block w-max primary-btn !text-[14px] px-8 text-base py-3 h-max"
              >
                See Details
              </Button>
            )}
          </div>

          <div className="line-clamp-2 lg:line-clamp-1 sm:!line-clamp-2">
            {flattenRichText(aboutBranch)}
          </div>
          {!variant && (
            <Button
              href={`/branches/?branch=${slugwithid}`}
              className="block w-max primary-btn !text-[14px] px-8 text-base py-3 mr-auto"
            >
              See Details
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface ExtendedBranchExtra extends BranchExtra {
  titleAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"; // Added titleAs
  addressAs?: "h3" | "h4" | "h5" | "h6" | "span"; // Added addressAs
}

export const BranchCard2: React.FC<ExtendedBranchExtra> = ({
  coverImage,
  id,
  branchName,
  address,
  aboutBranch,
  variant,
  titleAs = "h3", // Default to h3 if not provided
  addressAs = "h4", // Default to h4 if not provided
}) => {
  const slugwithid = generateSlugWithId(Number(id), branchName);

  const TitleTag = titleAs; // Dynamically set the tag for branch name
  const AddressTag = addressAs; // Dynamically set the tag for address
  return (
    <div
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className="parent bg-white sm:p-4 p-8 rounded-xl shadow-md"
    >
      <div className="flex gap-6">
        <div className={`flex flex-col w-full justify-between variant gap-5`}>
          <div className="flex w-full justify-between">
            <div className={`first flex-1 flex gap-8`}>
              <Image
                src={coverImage}
                height={112}
                width={112}
                alt={`${branchName} image`}
                className="first h-[7rem] min-h-full block aspect-square object-cover rounded-md"
              />

              <div className="i flex-1 lg:flex-col flex justify-between">
                <div className="first">
                  <TitleTag className="h5 !font-bold text-secondary-main">
                    {branchName}
                  </TitleTag>
                  <AddressTag className="text-label-main h5 flex items-center gap-1">
                    <div className="w-auto">
                      {variant ? <AddressIconVariant /> : <AddressIcon />}
                    </div>
                    <span className="line-clamp-1">{address}</span>
                  </AddressTag>
                </div>
                <Button
                  href={`/branches/?branch=${slugwithid}`}
                  className="block w-max whitespace-nowrap primary-btn !text-[14px] px-8 text-base py-3 h-max"
                >
                  See Details
                </Button>
              </div>
            </div>
          </div>

          <div className="line-clamp-2 lg:line-clamp-1 sm:!line-clamp-2">
            {flattenRichText(aboutBranch)}
          </div>
          {!variant && (
            <Button
              href={`/branches/?branch=${slugwithid}`}
              className="block w-max primary-btn !text-[14px] px-8 text-base py-3 mr-auto"
            >
              See Details
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const FirstItem: React.FC<{ desc?: string; loadingText: boolean }> = ({
  desc,
  loadingText,
}) => {
  const { data: branches, isLoading } = useBranches();
  return (
    <div className="flex flex-col gap-4">
      {!loadingText && !desc ? null : (
        <ParentCard headingTag="h3" variant header="About Us">
          <ShowMoreText
            loading={loadingText}
            maxLength={300}
            text={desc || "No description available"}
          ></ShowMoreText>
        </ParentCard>
      )}

      <ParentCard variant header="Branches">
        <CardGrid className="divide-y divide-themeColor" gap={0} maxColumns={1}>
          {isLoading ? (
            <div className="flex flex-col gap-4">
              <BranchCardSkeleton count={8} />
            </div>
          ) : branches && branches.length > 0 ? (
            branches.map((item, index) => (
              <div key={index} className="py-4">
                <BranchCard {...item} />
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">
              We’re currently updating our branch details. Soon, you’ll be able
              to view the locations, contact information, and services offered
              by our various branches across the region. Please check back
              shortly for the latest updates.
            </p>
          )}
        </CardGrid>
      </ParentCard>
    </div>
  );
};

export const SecondItem = () => {
  const { data: reviews } = useFetchReviews();
  return <ReviewParent variant reviews={reviews as ReviewType[]} />;
};

const AddressIcon = () => {
  return (
    <svg
      width="18"
      height="19"
      viewBox="0 0 18 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 8.375C9.62132 8.375 10.125 7.87132 10.125 7.25C10.125 6.62868 9.62132 6.125 9 6.125C8.37868 6.125 7.875 6.62868 7.875 7.25C7.875 7.87132 8.37868 8.375 9 8.375Z"
        fill="var(--primary-color)"
      />
      <path
        d="M9 1.625C5.89852 1.625 3.375 4.03848 3.375 7.00391C3.375 8.41613 4.01871 10.2942 5.2882 12.586C6.30773 14.4261 7.48723 16.09 8.1007 16.918C8.20437 17.0594 8.33991 17.1745 8.49633 17.2538C8.65276 17.3331 8.82567 17.3744 9.00105 17.3744C9.17644 17.3744 9.34935 17.3331 9.50578 17.2538C9.6622 17.1745 9.79774 17.0594 9.90141 16.918C10.5138 16.09 11.6944 14.4261 12.7139 12.586C13.9813 10.2949 14.625 8.41684 14.625 7.00391C14.625 4.03848 12.1015 1.625 9 1.625ZM9 9.5C8.55499 9.5 8.11998 9.36804 7.74997 9.12081C7.37996 8.87357 7.09157 8.52217 6.92127 8.11104C6.75097 7.6999 6.70642 7.2475 6.79323 6.81105C6.88005 6.37459 7.09434 5.97368 7.40901 5.65901C7.72368 5.34434 8.12459 5.13005 8.56105 5.04323C8.9975 4.95642 9.4499 5.00097 9.86104 5.17127C10.2722 5.34157 10.6236 5.62996 10.8708 5.99997C11.118 6.36998 11.25 6.80499 11.25 7.25C11.2493 7.84654 11.0121 8.41846 10.5903 8.84027C10.1685 9.26209 9.59654 9.49935 9 9.5Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

const AddressIconVariant = () => {
  return (
    <svg
      width="15"
      height="19"
      viewBox="0 0 15 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1293_180500)">
        <path
          d="M7.04314 10.6332C8.33981 10.6332 9.39096 9.58199 9.39096 8.28533C9.39096 6.98866 8.33981 5.9375 7.04314 5.9375C5.74647 5.9375 4.69531 6.98866 4.69531 8.28533C4.69531 9.58199 5.74647 10.6332 7.04314 10.6332Z"
          stroke="#606060"
          stroke-width="1.3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M7.04212 2.02344C5.38163 2.02344 3.78916 2.68306 2.61502 3.8572C1.44088 5.03134 0.78125 6.62382 0.78125 8.28431C0.78125 9.765 1.09586 10.7339 1.95516 11.806L7.04212 17.6756L12.1291 11.806C12.9884 10.7339 13.303 9.765 13.303 8.28431C13.303 6.62382 12.6434 5.03134 11.4692 3.8572C10.2951 2.68306 8.70261 2.02344 7.04212 2.02344Z"
          stroke="#606060"
          stroke-width="1.3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1293_180500">
          <rect
            width="14.087"
            height="18"
            fill="white"
            transform="translate(0 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
